import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useNotificationManager } from "../hooks/useNotificationHook";
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Box, Chip, Divider, Typography, CssBaseline } from "@mui/material";
import AppTheme from "./shared-theme/AppTheme";

import http from 'axios'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isNotificationIconOpen, setIsNotificationIconOpen] = useState(false);
  const [isAdminIconOpen, setIsAdminIconOpen] = useState(false);
  const [isUnreadExist, setIsUnreadExist] = useState(false);
  const { useFetchUnreadNotifications, useFetchNewsUpdates, updateLastSeen } = useNotificationManager(localStorage.getItem("is_user_logged_in") === "true");
  const navigate = useNavigate();

  const { data: newsUpdates } = useFetchNewsUpdates(0);
  const { data: unreadNotifications } = useFetchUnreadNotifications(Number(localStorage.getItem("id")));

  const aboutRef = useRef<HTMLDivElement | null>(null);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const adminRef = useRef<HTMLDivElement | null>(null);


  const closeDropdowns = (menu: string) => {
    setIsOpen(menu === "profile");
    setIsAboutOpen(menu === "about");
    setIsNotificationIconOpen(menu === "news-and-updates");
    setIsAdminIconOpen(menu === "admin");
  };

  const logout = async () => {
    try {
      await http.get("/api/auth/logout");
    } catch (err) {}
    localStorage.clear();
    navigate("/signin");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (
        (aboutRef.current && target && !aboutRef.current.contains(target)) ||
        (profileRef.current && target && !profileRef.current.contains(target)) ||
        (notificationRef.current && target && !notificationRef.current.contains(target)) ||
        (adminRef.current && target && !adminRef.current.contains(target))
      ) {
        closeDropdowns("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (unreadNotifications?.data && Array.isArray(unreadNotifications.data)) {
      setIsUnreadExist(unreadNotifications.data.length > 0);
    } else {
      setIsUnreadExist(false);
    }
  }, [unreadNotifications]);
  const handleNotificationClick = () => {
    updateLastSeen(Number(localStorage.getItem("id")));
    setIsNotificationIconOpen(false);
    setIsUnreadExist(false);
  };

  return (
    <>
      <CssBaseline enableColorScheme />
      <nav className="w-full flex px-8 shadow-lg relative">
        <div className="pt-3 pb-2 border-b-4 border-transparent hover:border-black">
          <NavLink className="text-3xl font-semibold" to="/">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="logo" className="max-h-10" />
              <img src="/logo-clemson.png" alt="logo" className="max-h-8" />
            </div>
          </NavLink>
        </div>
        {localStorage.getItem("is_user_logged_in") === "true" && (
          <div className="ml-auto flex text-lg relative">
            <ul className="flex">
              <Divider orientation="vertical" flexItem />
              {localStorage.getItem("is_admin") === "true" && (
                <li className="relative">
                  <div
                    className="flex items-center h-full py-4 px-2 text-black font-semibold text-sm hover:border-black hover:border-b-4 cursor-pointer"
                    onClick={() => {
                      setIsAdminIconOpen(!isAdminIconOpen);
                      closeDropdowns("admin");
                    }}
                  >
                    <span>Admin</span>
                  </div>
                  {isAdminIconOpen && (
                    <div ref={adminRef} className="absolute w-36 rounded-lg text-sm bg-white shadow-lg mt-2">
                      <ul>
                        <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer">
                          <NavLink to="/admin" onClick={() => setIsAdminIconOpen(false)}>Admin</NavLink>
                        </li>
                        <Divider sx={{ m: 1 }} />
                        <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer">
                          <NavLink to="/admin/news-updates/publish" onClick={() => setIsAdminIconOpen(false)}>
                            Publish News and Updates
                          </NavLink>
                        </li>
                      </ul>
                    </div>
                  )}
                </li>
              )}
              <Divider orientation="vertical" flexItem />
              <li className="relative">
                <div
                  className="flex items-center h-full py-4 px-2 text-black font-semibold text-sm hover:border-black hover:border-b-4 cursor-pointer"
                  onClick={() => {
                    setIsAboutOpen(!isAboutOpen);
                    closeDropdowns("about");
                  }}
                >
                  <span>About Us</span>
                </div>
                {isAboutOpen && (
                  <div ref={aboutRef} className="absolute w-36 rounded-lg text-sm bg-white shadow-lg mt-2">
                    <ul>
                      <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer">
                        <NavLink to="/about/terms" onClick={() => setIsAboutOpen(false)}>About</NavLink>
                      </li>
                      <Divider sx={{ m: 1 }} />
                      <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer">
                        <NavLink to="/about/news-updates" onClick={handleNotificationClick}>
                          News and Updates
                        </NavLink>
                      </li>
                      <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer">
                        <NavLink to="/about/contact-us" onClick={() => setIsAboutOpen(false)}>Contact Us</NavLink>
                      </li>
                      <li className="hover:bg-gray-200 px-2 py-1 mb-1 cursor-pointer">
                        <NavLink to="/about/terms" onClick={() => setIsAboutOpen(false)}>Terms Of Use</NavLink>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
              <Divider orientation="vertical" flexItem />
              <li className="relative">
                <div
                  className="flex items-center h-full py-4 px-2 text-black font-semibold text-sm hover:border-black hover:border-b-4 cursor-pointer"
                  onClick={() => {
                    setIsOpen(!isOpen);
                    closeDropdowns("profile");
                  }}
                >
                  <AccountCircleIcon width={24} height={24} />
                </div>
                {isOpen && (
                  <div ref={profileRef} className="absolute w-36 rounded-lg text-sm bg-white shadow-lg mt-2 right-0">
                    <ul>
                      <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer">
                        <NavLink to="/account/profile" onClick={() => setIsOpen(false)}>Profile</NavLink>
                      </li>
                      <Divider sx={{ m: 1 }} />
                      <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer">
                        <div onClick={logout}>Logout</div>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
              <Divider orientation="vertical" flexItem />
              <li className="relative">
                <div
                  className="flex items-center h-full py-4 px-2 text-black font-semibold text-sm hover:border-black hover:border-b-4 cursor-pointer"
                  onClick={() => {
                    setIsNotificationIconOpen(!isNotificationIconOpen);
                    closeDropdowns("news-and-updates");
                  }}
                >
                  <NotificationsIcon color="inherit" />
                  {isUnreadExist && (
                    <span className="absolute top-[1.2rem] right-2 bg-red-500 text-white text-xs rounded-full w-3 h-3" />
                  )}
                </div>
                {isNotificationIconOpen && (
                  <div ref={notificationRef} className="absolute w-48 rounded-lg text-sm bg-white shadow-lg mt-2 right-0">
                    <ul>
                      {newsUpdates?.data?.length === 0 || newsUpdates?.data === null ? (
                        <li>No updates available</li>
                      ) : (
                        newsUpdates?.data?.slice(0, 5).map((notification) => (
                          <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer" key={notification.id}>
                            <NavLink to="/about/news-updates" onClick={handleNotificationClick}>
                              {notification.title}
                            </NavLink>
                          </li>
                        ))
                      )}
                      <li className="hover:bg-gray-200 px-2 py-1 cursor-pointer font-light bg-gray-100">
                        <NavLink to="/about/news-updates" onClick={handleNotificationClick}>
                          <u>More</u>
                        </NavLink>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
              <Divider orientation="vertical" flexItem />
            </ul>
          </div>
        )}
      </nav>
    </>
  );
}
